/*
 * Menu mobile (snippets/header-drawer.liquid).
 *
 * Abrir e fechar a gaveta continua com o HeaderDrawer (global.js). Aqui ficam a troca
 * entre a tela principal e a tela de subcategorias, o botão "fechar" e os selos de
 * desconto automáticos.
 *
 * Os selos vêm da seção do cabeçalho em /?view=ec-mobile-menu-badges (data-badges-url),
 * buscada na primeira abertura e guardada por 10 minutos no sessionStorage.
 *
 * O conteúdo de cada tela de subcategoria vem num <template data-ec-mm-panel-content> e
 * só entra no DOM quando a categoria é aberta pela primeira vez (stampPanel).
 */
if (!customElements.get('ec-mobile-menu')) {
  const EC_MM_BADGES_TTL = 10 * 60 * 1000;

  customElements.define(
    'ec-mobile-menu',
    class EcMobileMenu extends HTMLElement {
      connectedCallback() {
        this.details = this.closest('details');
        this.mainView = this.querySelector('[data-ec-mm-view="main"]');
        this.closeButton = this.querySelector('[data-ec-mm-control="close"]');
        this.backButton = this.querySelector('[data-ec-mm-control="back"]');
        this.activePanel = null;
        this.activeOpener = null;
        this.mainScrollTop = 0;

        this.header = this.closest('header');
        this.summary = this.details?.querySelector('summary');

        this.onClick = this.onClick.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onToggle = this.onToggle.bind(this);
        this.onSummaryClick = this.onSummaryClick.bind(this);
        this.updateTop = this.updateTop.bind(this);

        this.addEventListener('click', this.onClick);
        this.addEventListener('keyup', this.onKeyUp);
        this.details?.addEventListener('toggle', this.onToggle);
        this.summary?.addEventListener('click', this.onSummaryClick);
        window.addEventListener('resize', this.updateTop);

        this.badgesUrl = this.dataset.badgesUrl;
        this.badgesRequested = false;
        const cachedBadges = this.readCachedBadges();
        if (cachedBadges) {
          this.badgesRequested = true;
          this.applyBadges(cachedBadges);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keyup', this.onKeyUp);
        this.details?.removeEventListener('toggle', this.onToggle);
        this.summary?.removeEventListener('click', this.onSummaryClick);
        window.removeEventListener('resize', this.updateTop);
      }

      onSummaryClick() {
        this.updateTop();
        this.loadBadges();
      }

      // O menu começa logo abaixo da barra do cabeçalho. O --header-bottom-position do
      // HeaderDrawer mede a seção inteira, que inclui a faixa de categorias abaixo da barra.
      // No toque que abre o menu, o global.js já leu a posição na fase de captura, antes
      // de o HeaderDrawer mexer em classe; ler de novo aqui forçaria outro recálculo da página.
      updateTop() {
        if (!this.header) return;
        const bottom = window.ecMobileMenuTop != null
          ? window.ecMobileMenuTop
          : Math.round(this.header.getBoundingClientRect().bottom);
        window.ecMobileMenuTop = null;
        this.style.setProperty('--ec-mm-top', `${bottom}px`);
      }

      onClick(event) {
        this.trackMenuClick(event);

        const control = event.target.closest('[data-ec-mm-control]');
        if (!control || !this.contains(control)) return;

        switch (control.dataset.ecMmControl) {
          case 'open':
            this.openPanel(control);
            break;
          case 'back':
            this.closePanel({ restore: true });
            break;
          case 'close':
            // Fechar pelo summary deixa o HeaderDrawer limpar o cabeçalho e devolver o foco.
            this.details?.querySelector('summary')?.click();
            break;
        }
      }

      // Clique no menu vira o evento "menu_click" no dataLayer, no mesmo formato do
      // "menu_click_atendimento" do ec-whatsapp-dropdown. Com ele, o GA4 do menu (tag 199)
      // pode trocar o acionador "clique em todos os elementos", que faz o Tag Manager avaliar
      // gatilhos e variáveis a cada toque na página, por um evento personalizado
      // (ajustes-performance-dev-rev3, item 2.7b). menu_acao separa link de abrir categoria.
      trackMenuClick(event) {
        const link = event.target.closest('a[href]');
        const opener = link ? null : event.target.closest('[data-ec-mm-control="open"]');
        const item = link || opener;
        if (!item || !this.contains(item)) return;

        const text = (item.getAttribute('aria-label') || item.textContent || '').replace(/\s+/g, ' ').trim();
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'menu_click',
          menu_item: text.slice(0, 100),
          menu_url: link ? link.getAttribute('href') : '',
          menu_acao: link ? 'link' : 'abrir_categoria',
          menu_local: 'mobile'
        });
      }

      onKeyUp(event) {
        if (event.code?.toUpperCase() !== 'ESCAPE' || !this.activePanel) return;

        // Esc numa subcategoria volta um nível; sem isto o HeaderDrawer fecharia a gaveta.
        event.stopPropagation();
        this.closePanel({ restore: true });
      }

      onToggle() {
        // A gaveta sempre reabre na tela principal.
        if (!this.details.open) this.closePanel({ restore: false });
      }

      openPanel(opener) {
        const panel = document.getElementById(opener.getAttribute('aria-controls'));
        if (!panel) return;

        this.stampPanel(panel);
        this.mainScrollTop = this.scrollTop;
        this.mainView.hidden = true;
        this.show(panel, 'forward');
        this.activePanel = panel;
        this.activeOpener = opener;
        opener.setAttribute('aria-expanded', 'true');
        this.closeButton.hidden = true;
        this.backButton.hidden = false;
        this.scrollTop = 0;
        this.backButton.focus({ preventScroll: true });
      }

      closePanel({ restore }) {
        const panel = this.activePanel;
        const opener = this.activeOpener;
        if (!panel) return;

        panel.hidden = true;
        this.activePanel = null;
        this.activeOpener = null;
        opener.setAttribute('aria-expanded', 'false');
        this.backButton.hidden = true;
        this.closeButton.hidden = false;

        if (restore) {
          this.show(this.mainView, 'back');
          this.scrollTop = this.mainScrollTop;
          opener.focus({ preventScroll: true });
        } else {
          this.mainView.hidden = false;
          this.scrollTop = 0;
        }
      }

      // O conteúdo dos painéis chega num <template data-ec-mm-panel-content>
      // (header-drawer.liquid) e só entra no DOM na primeira abertura.
      panelTemplate(panel) {
        return panel.querySelector(':scope > template[data-ec-mm-panel-content]');
      }

      stampPanel(panel) {
        const template = this.panelTemplate(panel);
        if (!template) return;
        panel.append(template.content);
        template.remove();
        // Selos automáticos que já chegaram valem também para o painel recém-montado.
        if (this.discounts) this.applyBadges(this.discounts);
      }

      loadBadges() {
        if (!this.badgesUrl || this.badgesRequested) return;
        this.badgesRequested = true;

        fetch(this.badgesUrl)
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .then((html) => {
            const data = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ec-mm-discounts]');
            if (!data) return;
            const discounts = JSON.parse(data.textContent);
            this.writeCachedBadges(discounts);
            this.applyBadges(discounts);
          })
          .catch(() => {
            // Sem selos desta vez; a próxima abertura tenta de novo.
            this.badgesRequested = false;
          });
      }

      // discounts: { "<url do link>": <maior desconto %> }. Categoria com submenu mostra o
      // maior valor entre as subcategorias do painel dela.
      applyBadges(discounts) {
        this.discounts = discounts;
        this.querySelectorAll('[data-ec-mm-badge-url]').forEach((slot) => {
          this.setBadge(slot, discounts[slot.dataset.ecMmBadgeUrl]);
        });
        this.querySelectorAll('[data-ec-mm-badge-panel]').forEach((slot) => {
          const panel = document.getElementById(slot.dataset.ecMmBadgePanel);
          // Painel ainda não montado: os links estão no conteúdo do <template>.
          const root = panel && (this.panelTemplate(panel)?.content || panel);
          const links = root ? root.querySelectorAll('.ec-mm__sub-link') : [];
          const values = Array.from(links, (link) => discounts[link.getAttribute('href')] || 0);
          this.setBadge(slot, Math.max(0, ...values));
        });
      }

      setBadge(slot, discount) {
        slot.querySelector(':scope > .ec-mm__badge')?.remove();
        if (!(discount > 0)) return;

        // A cor vem do bloco da categoria e independe de o campo "Selo" estar preenchido.
        const badge = document.createElement('span');
        badge.className = `ec-mm__badge ec-mm__badge--${slot.dataset.ecMmBadgeStyle || 'amarelo'}`;
        badge.textContent = `-${discount}%`;
        slot.append(badge);
      }

      badgesCacheKey() {
        return `ec-mm-badges:${this.badgesUrl}`;
      }

      readCachedBadges() {
        if (!this.badgesUrl) return null;
        try {
          const cached = JSON.parse(sessionStorage.getItem(this.badgesCacheKey()));
          if (cached && Date.now() - cached.savedAt < EC_MM_BADGES_TTL) return cached.discounts;
        } catch (error) {
          // sessionStorage indisponível (aba anônima, bloqueio): segue sem cache.
        }
        return null;
      }

      writeCachedBadges(discounts) {
        try {
          sessionStorage.setItem(this.badgesCacheKey(), JSON.stringify({ savedAt: Date.now(), discounts }));
        } catch (error) {
          // Idem: sem cache, o menu busca de novo na próxima página.
        }
      }

      show(view, direction) {
        view.hidden = false;
        // Reinicia a animação caso a mesma tela seja reaberta antes de ela terminar.
        delete view.dataset.ecMmEnter;
        void view.offsetWidth;
        view.dataset.ecMmEnter = direction;
        view.addEventListener('animationend', () => delete view.dataset.ecMmEnter, { once: true });
      }
    }
  );
}
